import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { getChangelogBody } from './server'
import { TabContentDefaultWrapper } from '../wrapper'
import { useTabsStore } from '../../explore/components/tabs/store'
import { BreadcrumbStickyContainer } from '../../explore/components/tabs/dashboard-breadcrumb'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { Breadcrumb, BreadcrumbItem } from '~/sotano-kit/primitives/breadcrumb'
import { useUserStore } from '~/app/_store/user-store'
import { useSetLastChangelogSeen } from './use-last-changelog-seen'

export const ChangelogEditorEntry = ({ entryId }: { entryId: string }) => {
  const { data, isInitialLoading } = useQuery(
    ['changelog', entryId] as const,
    async ({ queryKey }) => {
      return await getChangelogBody(queryKey[1])
    },
    { staleTime: Infinity }
  )

  const setLastChangelogSeen = useSetLastChangelogSeen()

  const user = useUserStore((s) => (s.isSyncing ? null : s.optimisticUser))
  const removeTab = useTabsStore((s) => s.removeTab)

  React.useEffect(() => {
    if (!user || !data?.isNew || user.lastChangelogSeen === entryId) return

    setLastChangelogSeen(entryId)
  }, [data?.isNew, entryId, setLastChangelogSeen, user])

  React.useEffect(() => {
    if (data || isInitialLoading) return
    removeTab([`changelog:${entryId}`])
  }, [entryId, data, isInitialLoading, removeTab])

  return (
    <div className="flex flex-col flex-1 w-full">
      <BreadcrumbStickyContainer>
        <Breadcrumb>
          <BreadcrumbItem active>{data?.title}</BreadcrumbItem>
        </Breadcrumb>
      </BreadcrumbStickyContainer>
      <TabContentDefaultWrapper size="xs">
        {isInitialLoading ? <EntryLoader /> : data?.jsx}
      </TabContentDefaultWrapper>
    </div>
  )
}

const EntryLoader = () => {
  return (
    <>
      <div className="space-y-6">
        <Skeleton size="lg" height={32} width={200} borderless />
        <Skeleton size="lg" height={120} width="100%" borderless />
        <Skeleton size="lg" height={48} width="100%" borderless />
        <Skeleton size="lg" height={30} width={120} borderless />
      </div>
      <div className="space-y-9 mt-16">
        <Skeleton size="lg" height={428} width="100%" borderless />
        <Skeleton size="lg" height={120} width="100%" borderless />
        <Skeleton size="lg" height={84} width="100%" borderless />
      </div>
    </>
  )
}
